import { Component } from 'react';
import Head from 'next/head';
import dynamic from 'next/dynamic';
import Link from 'next/link';
import { ReactSVG } from 'react-svg';
import key from 'weak-key';
import SiteSettings from '~/siteSettings';
import { getGalleries, getIcon } from '~/utils/utils';
import PageWrapper from '~/components/wrappers/pageWrapper';

const Utag = dynamic(() => import('~/components/utag/utag'), { ssr: false });

/**
 * Galleries
 * @category Page
 * @extends Component
 * @summary Display the galleries listing page.
 * @return {Node} React node containing the galleries listing page.
 */

class Galleries extends Component {
  constructor(props) {
    super(props);
    this.state = {
      itemsPerPage: 8,
      offset: 0,
      data: props.data,
      loading: false
    };
  }

  getMore = async () => {
    const { itemsPerPage, offset, data } = this.state;
    const incrementOffset = offset + itemsPerPage;

    this.setState({
      loading: true
    });

    const updatedData = await getGalleries({
      machine_name: 'latest_content_by_type',
      display: 'master',
      itemsPerPage,
      offset: incrementOffset,
      viewArgs: [
        'gallery'
      ]
    });

    this.setState({
      loading: false,
      offset: incrementOffset,
      data: {
        ...data,
        viewQuery: {
          count: data.viewQuery.count,
          resultRow: data.viewQuery.resultRow.concat(updatedData.viewQuery.resultRow)
        }
      }
    });
  }

  render() {
    const { baseUrl, sitePrimaryRsid } = this.props;
    const { data, loading } = this.state;
    const { resultRow } = data.viewQuery;
    const path = '/galleries';

    return (
      <>
        <Head>
          <title>Galleries | { SiteSettings.siteName }</title>
          <meta name='title' content='Galleries' />
          <link rel='canonical' href={`${ baseUrl }${ path }`} />
          <meta name="keywords" content={ SiteSettings.defaultKeywords } />
        </Head>
        <div className='constrain'>
          <div className='galleries__header'>
            <div className='galleries__header-icon'>
              <ReactSVG src='/img/icons/gallery.svg' />
            </div>
            <h1>Photo Galleries</h1>
          </div>
          <div className='block-see-more__grid'>
            { resultRow.map((item) => (
              <Link legacyBehavior href={ item.path } key={`see-more-item-${key(item)}`}>
                <a aria-label={`Visit ${ item.title }`} className='card-interior'>
                  <picture>
                    <source media={ `(max-width: ${ SiteSettings.breakpoints.sm })` } srcSet={ item.field_image.field_image.sm.url } />
                    <img
                      src={ item.field_image.field_image.lg.url }
                      alt={ item.field_image?.alt }
                      width={ item.field_image.field_image.lg.width }
                      height={ item.field_image.field_image.lg.height }
                      className='card-interior__img'
                      loading='lazy'
                    />
                  </picture>
                  <h2>
                    <div className='card-interior__type-icon'>
                      <ReactSVG src={ getIcon(item.__typename) } />
                    </div>
                    { item.title }
                  </h2>
                  <div className='card-interior__mask'></div>
                </a>
              </Link>
            ))}
          </div>
          <div className='text-center'>
            { data.viewQuery.count > data.viewQuery.resultRow.length &&
              <button
                type='button'
                className='btn__cta'
                aria-label='Load More'
                onClick={ this.getMore }
              >
                <span>
                  { loading && <img src='/img/loader.gif' alt='Loading' /> } see more
                </span>
              </button>
            }
          </div>
        </div>
        <div className='spacer spacer--2'></div>
        <Utag pageType='taxonomy' siteSection='taxonomy' data={{ sitePrimaryRsid }} />
      </>
    );
  }
}

export async function getServerSideProps() {
  const data = await getGalleries({
    machine_name: 'latest_content_by_type',
    display: 'master',
    itemsPerPage: 8,
    offset: 0,
    viewArgs: [
      'gallery'
    ]
  });

  return {
    props: {
      data
    }
  }
}

export default PageWrapper(Galleries);
